// Role-Based Access Control utilities
import { loadPagePermissions } from './permissions-config';

export const ROLES = {
  NONE: 'none',
  STOCKTAKE_CHAMPION: 'stocktake_champion',
  STOCKTAKE_CONTROLLER: 'stocktake_controller',
  STOCKTAKE_AUDITOR: 'stocktake_auditor',
  STORE_MANAGER: 'store_manager',
  COMPLEX_MANAGER: 'complex_manager',
  REGIONAL_MANAGER: 'regional_manager',
  NETWORK_ADMIN: 'network_admin'
};

// Role hierarchy (higher index = more permissions)
const ROLE_HIERARCHY = [
  ROLES.NONE,
  ROLES.STOCKTAKE_CHAMPION,
  ROLES.STOCKTAKE_AUDITOR,
  ROLES.STOCKTAKE_CONTROLLER,
  ROLES.STORE_MANAGER,
  ROLES.COMPLEX_MANAGER,
  ROLES.REGIONAL_MANAGER,
  ROLES.NETWORK_ADMIN
];

// Check if user has a specific role or higher
export const hasRole = (userRole, requiredRole) => {
  const userIndex = ROLE_HIERARCHY.indexOf(userRole);
  const requiredIndex = ROLE_HIERARCHY.indexOf(requiredRole);
  return userIndex >= requiredIndex;
};

// Check if user is network admin
export const isNetworkAdmin = (user) => {
  return user?.role === ROLES.NETWORK_ADMIN;
};

// Check if user can manage their own store
export const isStoreManager = (user) => {
  return hasRole(user?.role, ROLES.STORE_MANAGER);
};

// Check if user can manage devices
export const canManageDevices = (user) => {
  return hasRole(user?.role, ROLES.STOCKTAKE_CHAMPION);
};

// Check if user can schedule stocktakes
export const canScheduleStocktakes = (user) => {
  return hasRole(user?.role, ROLES.STOCKTAKE_CONTROLLER);
};

// Check if user can approve verifications
export const canApproveVerifications = (user) => {
  return hasRole(user?.role, ROLES.STOCKTAKE_AUDITOR);
};

// Check if user can create users
export const canCreateUsers = (user) => {
  return isNetworkAdmin(user);
};

// Check if user can create stores
export const canCreateStores = (user) => {
  return isNetworkAdmin(user);
};

// Check if user can access settings
export const canAccessSettings = (user) => {
  return hasRole(user?.role, ROLES.STORE_MANAGER);
};

// Check if user can access full settings
export const canAccessFullSettings = (user) => {
  return isNetworkAdmin(user);
};

// Check if user should see only their store data
export const isStoreRestricted = (user) => {
  return user?.store_id && !hasRole(user?.role, ROLES.COMPLEX_MANAGER);
};

// Filter data by store access
export const filterByStoreAccess = (data, user, storeIdField = 'store_id') => {
  if (isNetworkAdmin(user)) {
    return data; // Network admin sees all
  }
  
  if (hasRole(user?.role, ROLES.REGIONAL_MANAGER)) {
    return data; // Regional manager sees all for now (can be region-filtered later)
  }
  
  if (hasRole(user?.role, ROLES.COMPLEX_MANAGER)) {
    return data; // Complex manager sees all for now (can be complex-filtered later)
  }
  
  if (user?.store_id) {
    // Filter to only user's store
    return data.filter(item => item[storeIdField] === user.store_id);
  }
  
  return data;
};

// Get current page permissions (loads from localStorage if customized)
const getPagePermissions = () => {
  return loadPagePermissions();
};

// Check if user can access a specific page
export const canAccessPage = (user, pathname) => {
  // Network admin always has access
  if (user?.role === ROLES.NETWORK_ADMIN) return true;
  
  const PAGE_PERMISSIONS = getPagePermissions();
  const allowedRoles = PAGE_PERMISSIONS[pathname];
  
  if (!allowedRoles) return true; // No restriction defined
  
  return allowedRoles.includes(user?.role);
};
